import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playSound } from '../lib/soundEffects';
import { estimateReadingTime } from '../lib/readingTime';
import { synthesizeReadingDigest } from '../lib/digestGenerator';
import { getFocusStats, recordReadingSession } from '../lib/focusTracker';

const {
  FiBookOpen, FiX, FiCheckCircle2, FiCircle, FiExternalLink, FiClock, FiTrash2,
  FiVolume2, FiPlay, FiPause, FiSquare,
} = FiIcons;

export default function ReadingListDrawer({
  isOpen,
  onClose,
  data = {},
  onOpenBookmark,
  onOpenNostr,
  themeColors: tc,
  isDark = true,
}) {
  const [completedIds, setCompletedIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('memark_read_completed') || '[]');
    } catch {
      return [];
    }
  });

  const [playingItemId, setPlayingItemId] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechRate, setSpeechRate] = useState(1.0);

  // Stop speech when drawer closes or unmounts, and handle Escape key
  useEffect(() => {
    if (!isOpen) {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      setIsSpeaking(false);
      setPlayingItemId(null);
      return;
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const stopSpeech = () => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
    setPlayingItemId(null);
  };

  const [autoplayQueue, setAutoplayQueue] = useState(false);
  const autoplayQueueRef = useRef(autoplayQueue);
  autoplayQueueRef.current = autoplayQueue;

  const playItemSpeech = (item, itemsList) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const cleanNote = (item.note || '').replace(/#\w+/g, '').trim();
    const textToSpeak = `${item.title}. ${cleanNote ? 'Notes: ' + cleanNote : ''}`;
    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.rate = speechRate;
    utterance.onend = () => {
      setIsSpeaking(false);
      setPlayingItemId(null);
      if (autoplayQueueRef.current && itemsList && itemsList.length > 0) {
        const currentIdx = itemsList.findIndex((it) => it.id === item.id);
        if (currentIdx !== -1 && currentIdx + 1 < itemsList.length) {
          const nextItem = itemsList[currentIdx + 1];
          setTimeout(() => {
            playSound('launch');
            playItemSpeech(nextItem, itemsList);
          }, 600);
        }
      }
    };
    utterance.onerror = () => {
      setIsSpeaking(false);
      setPlayingItemId(null);
    };

    setPlayingItemId(item.id);
    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
    playSound('launch');
  };

  const handleToggleSpeak = (item) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      alert('Speech synthesis is not supported in this browser.');
      return;
    }

    if (playingItemId === item.id && isSpeaking) {
      window.speechSynthesis.pause();
      setIsSpeaking(false);
      return;
    }

    if (playingItemId === item.id && !isSpeaking && window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setIsSpeaking(true);
      return;
    }

    playItemSpeech(item, readingItems);
  };

  const cycleSpeechRate = () => {
    const rates = [1.0, 1.25, 1.5, 2.0];
    const nextIdx = (rates.indexOf(speechRate) + 1) % rates.length;
    const nextRate = rates[nextIdx];
    setSpeechRate(nextRate);
    if (playingItemId && isSpeaking) {
      const activeItem = readingItems.find((i) => i.id === playingItemId);
      if (activeItem) {
        window.speechSynthesis.cancel();
        const cleanNote = (activeItem.note || '').replace(/#\w+/g, '').trim();
        const utterance = new SpeechSynthesisUtterance(`${activeItem.title}. ${cleanNote ? 'Notes: ' + cleanNote : ''}`);
        utterance.rate = nextRate;
        utterance.onend = () => { setIsSpeaking(false); setPlayingItemId(null); };
        window.speechSynthesis.speak(utterance);
      }
    }
  };

  // Collect reading list items
  const readingItems = useMemo(() => {
    const all = [];
    Object.values(data).forEach((folder) => {
      (folder.items || []).forEach((item) => {
        if (item.type === 'link') {
          const t = (item.title || '').toLowerCase();
          const n = (item.note || '').toLowerCase();
          const u = (item.url || '').toLowerCase();
          // Match articles, blogs, medium, substack, docs, reading tags
          if (
            t.includes('read') ||
            t.includes('article') ||
            t.includes('guide') ||
            t.includes('tutorial') ||
            n.includes('#read') ||
            n.includes('read later') ||
            u.includes('medium.com') ||
            u.includes('substack.com') ||
            u.includes('dev.to') ||
            u.includes('blog.') ||
            u.includes('/blog/')
          ) {
            all.push(item);
          }
        }
      });
    });
    return all;
  }, [data]);

  const toggleCompleted = (id) => {
    playSound('click');
    setCompletedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id];
      localStorage.setItem('memark_read_completed', JSON.stringify(next));
      return next;
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[80] flex justify-end">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        />

        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          className="relative w-full max-w-md h-full bg-[#121216] border-l border-[#26262e] shadow-2xl p-6 z-10 flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b border-[#23232c] shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                <SafeIcon icon={FiBookOpen} className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <span>Reading Queue</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
                    {readingItems.length} Saved
                  </span>
                </h3>
                <p className="text-xs text-[#777]">Articles, tutorials, and documentation to read</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-[#666] hover:text-white hover:bg-[#222] transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>

          {/* Smart Focus Velocity & Streak HUD */}
          <div className="mt-3 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-between text-[11px] font-mono">
            <div className="flex items-center gap-1.5 text-emerald-400 font-semibold">
              <span>⚡</span>
              <span>{getFocusStats().avgWpm} WPM Personal Velocity</span>
            </div>
            <div className="flex items-center gap-1.5 text-amber-300 font-semibold">
              <span>🔥</span>
              <span>{getFocusStats().streakDays}-Day Focus Streak</span>
            </div>
          </div>

          {/* AI Executive Briefing Synthesizer Bar */}
          {readingItems.length > 0 && (
            <div className="mt-3 flex items-center justify-between p-2.5 rounded-xl bg-purple-500/10 border border-purple-500/30 shadow-sm gap-2">
              <div className="text-xs text-purple-200 font-medium flex items-center gap-1.5 min-w-0 truncate">
                <span>✨</span>
                <span className="truncate">AI Briefing & Audio</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => {
                    playSound('click');
                    setAutoplayQueue((prev) => !prev);
                  }}
                  className={`px-2 py-1 rounded-lg text-[10px] font-mono cursor-pointer transition-all border ${
                    autoplayQueue
                      ? 'bg-emerald-500/30 text-emerald-300 border-emerald-500/50 shadow-[0_0_8px_rgba(16,185,129,0.3)]'
                      : 'bg-white/5 text-[#888] border-white/10 hover:text-white'
                  }`}
                  title="Toggle continuous queue playback like a podcast"
                >
                  🔁 {autoplayQueue ? 'Autoplay ON' : 'Autoplay'}
                </button>
                <button
                  onClick={() => {
                    playSound('launch');
                    const digest = synthesizeReadingDigest(readingItems);
                    navigator.clipboard.writeText(digest);
                    alert('Executive Reading Digest synthesized and copied to clipboard! You can paste it directly into Scratchpad (Alt+N) or Obsidian.');
                  }}
                  className="px-2.5 py-1 rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/40 text-[10px] font-mono cursor-pointer transition-all"
                  title="Synthesize and copy full markdown digest"
                >
                  📋 Copy Digest
                </button>
                <button
                  onClick={() => {
                    playSound('click');
                    if (onOpenNostr) onOpenNostr();
                  }}
                  className="px-2.5 py-1 rounded-lg bg-violet-500/20 text-violet-300 hover:bg-violet-500/30 border border-violet-500/40 text-[10px] font-mono cursor-pointer transition-all"
                  title="Broadcast sovereign reading list to decentralized Nostr relays (NIP-51)"
                >
                  ⚡ Nostr Pub
                </button>
              </div>
            </div>
          )}

          {/* Active Audio Narrator Bar */}
          {playingItemId && (
            <div className="mt-3 p-3 rounded-xl bg-purple-950/40 border border-purple-500/40 flex items-center justify-between gap-3 shadow-lg">
              <div className="flex items-center gap-2 min-w-0">
                <div className="flex items-end gap-0.5 h-4 w-4">
                  <span className={`w-1 bg-purple-400 rounded-full transition-all ${isSpeaking ? 'h-4 animate-pulse' : 'h-1.5'}`} />
                  <span className={`w-1 bg-purple-300 rounded-full transition-all ${isSpeaking ? 'h-3 animate-pulse delay-75' : 'h-2'}`} />
                  <span className={`w-1 bg-purple-500 rounded-full transition-all ${isSpeaking ? 'h-4 animate-pulse delay-150' : 'h-1'}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase font-mono text-purple-300 font-bold tracking-wider">Audio Narrator</p>
                  <p className="text-xs text-white truncate max-w-[190px]">
                    {readingItems.find((i) => i.id === playingItemId)?.title || 'Playing article'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={cycleSpeechRate}
                  className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 cursor-pointer"
                  title="Playback Rate"
                >
                  {speechRate}x
                </button>
                <button
                  onClick={() => {
                    const item = readingItems.find((i) => i.id === playingItemId);
                    if (item) handleToggleSpeak(item);
                  }}
                  className="p-1.5 rounded-lg bg-purple-500 text-black hover:bg-purple-400 cursor-pointer transition-colors"
                  title={isSpeaking ? "Pause" : "Resume"}
                >
                  <SafeIcon icon={isSpeaking ? FiPause : FiPlay} className="w-3 h-3 fill-current" />
                </button>
                <button
                  onClick={stopSpeech}
                  className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 cursor-pointer transition-colors"
                  title="Stop"
                >
                  <SafeIcon icon={FiSquare} className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {/* List */}
          <div className="flex-1 overflow-y-auto space-y-2 py-4 pr-1">
            {readingItems.length === 0 ? (
              <div className="py-20 text-center text-[#666] space-y-2">
                <SafeIcon icon={FiBookOpen} className="w-10 h-10 mx-auto text-[#444]" />
                <p className="text-sm font-semibold text-white">Your Reading Queue is Empty</p>
                <p className="text-xs max-w-xs mx-auto text-[#666]">
                  Bookmarks from Medium, Substack, Dev.to or with <span className="text-purple-400">#read</span> in notes will appear here.
                </p>
              </div>
            ) : (
              readingItems.map((item) => {
                const isDone = completedIds.includes(item.id);
                return (
                  <div
                    key={item.id}
                    className={`p-3 rounded-xl border transition-all flex items-start justify-between gap-3 ${
                      isDone
                        ? 'bg-[#141418]/60 border-[#222228] opacity-60'
                        : 'bg-[#17171e] border-[#262630] hover:border-purple-500/30'
                    }`}
                  >
                    <button
                      onClick={() => toggleCompleted(item.id)}
                      className="mt-0.5 p-1 rounded-md text-[#666] hover:text-purple-400 transition-colors"
                    >
                      <SafeIcon
                        icon={isDone ? FiCheckCircle2 : FiCircle}
                        className={`w-4 h-4 ${isDone ? 'text-purple-400' : ''}`}
                      />
                    </button>

                    <div className="min-w-0 flex-1">
                      <div
                        onClick={() => onOpenBookmark(item.url)}
                        className={`text-sm font-medium cursor-pointer truncate hover:text-purple-300 transition-colors ${
                          isDone ? 'line-through text-[#888]' : 'text-white'
                        }`}
                      >
                        {item.title}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[11px] text-[#777]">
                        <span className="truncate max-w-[180px]">{item.url.replace(/^https?:\/\//, '')}</span>
                        <span className="flex items-center gap-1 text-purple-400/80">
                          <SafeIcon icon={FiClock} className="w-2.5 h-2.5" />
                          <span>{estimateReadingTime(item)?.label || '3 min read'}</span>
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => handleToggleSpeak(item)}
                        className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                          playingItemId === item.id && isSpeaking
                            ? 'bg-purple-500/30 text-purple-300 border border-purple-500/40'
                            : 'text-[#777] hover:text-purple-300 hover:bg-white/5'
                        }`}
                        title={playingItemId === item.id && isSpeaking ? "Pause Narration" : "Listen to article (Speech Synthesis)"}
                      >
                        <SafeIcon icon={playingItemId === item.id && isSpeaking ? FiPause : FiVolume2} className="w-3.5 h-3.5" />
                      </button>

                      <button
                        onClick={() => onOpenBookmark(item.url)}
                        className="p-1.5 rounded-lg text-[#666] hover:text-white hover:bg-white/5 transition-colors shrink-0 cursor-pointer"
                        title="Open external link"
                      >
                        <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
